import React from "react"

import { Info } from "lucide-react"

import { Button } from "@/components/ui/Button"

import { PricingTooltip } from "./Tooltip"

interface Column {
  title: string
  description: string
}

interface Feature {
  name: string
  values: string[]
  link?: () => void
  infoTooltip?: React.ReactNode
}

interface CTAButton {
  text: string
  variant: "default" | "outline" | "secondary" | "destructive" | "ghost"
  onClick: () => void
}

interface FeaturesTableProps {
  columns: Column[]
  features: Feature[]
  ctaButtons: CTAButton[]
}

export function FeaturesTable({
  columns,
  features,
  ctaButtons,
}: FeaturesTableProps) {
  return (
    <div className="tw-overflow-x-auto tw-rounded-2xl tw-shadow-sm tw-border-black tw-border-2">
      <table className="compare-table tw-w-full tw-table-fixed">
        <thead>
          <tr>
            <th className="tw-w-1/4 tw-text-left tw-pl-8 tw-py-4"></th>
            {columns.map((column, index) => (
              <th
                key={index}
                className="tw-w-1/4 tw-text-2xl tw-font-semibold tw-text-gray-800 tw-p-6"
              >
                {column.title}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {/* Subtitle row */}
          <tr>
            <td className="tw-text-left tw-pl-8"></td>
            {columns.map((column, index) => (
              <td
                key={index}
                className="tw-text-gray-600 tw-text-sm tw-leading-relaxed tw-px-5 tw-pt-0"
              >
                {column.description}
              </td>
            ))}
          </tr>

          {/* CTA row */}
          <tr>
            <td className="tw-text-left tw-pl-8"></td>
            {ctaButtons.map((button, index) => (
              <td key={index} className="tw-pb-8 table-header-row">
                <Button
                  className={`tw-w-full tw-py-4 tw-text-center tw-text-sm tw-font-semibold tw-border tw-rounded-[2px] ${
                    button.variant === "outline"
                      ? "tw-border-gray-200 tw-bg-white tw-text-gray-800 tw-transition-colors tw-duration-300 tw-hover:bg-gray-50"
                      : ""
                  }`}
                  variant={button.variant}
                  size="lg"
                  onClick={button.onClick}
                >
                  {button.text}
                </Button>
              </td>
            ))}
          </tr>

          {/* Divider */}
          <tr>
            <td
              colSpan={columns.length + 1}
              className="tw-h-px tw-p-0 tw-bg-gray-100"
            ></td>
          </tr>

          {/* Features */}
          {features.map((feature, featureIndex) => (
            <tr key={featureIndex} className="tw-border-black-50 tw-border">
              <td className="tw-text-left tw-pl-8 tw-py-5 tw-text-gray-800 tw-text-sm tw-font-medium">
                {feature.link ? (
                  <Button
                    className="tw-pl-1 tw-pr-2 tw-text-base"
                    variant="link"
                    size="sm"
                    onClick={feature.link}
                  >
                    {feature.name}
                  </Button>
                ) : (
                  feature.name
                )}

                {feature.infoTooltip && (
                  <PricingTooltip content={feature.infoTooltip}>
                    <Info size={8} />
                  </PricingTooltip>
                )}
              </td>
              {feature.values.map((value, valueIndex) => (
                <td
                  key={valueIndex}
                  className="tw-py-5 tw-text-base tw-text-gray-800"
                >
                  {value}
                </td>
              ))}
            </tr>
          ))}

          {/* Divider */}
          <tr>
            <td
              colSpan={columns.length + 1}
              className="tw-h-px tw-p-0 tw-bg-gray-100"
            ></td>
          </tr>
        </tbody>
      </table>
    </div>
  )
}
